import { useRef, useMemo, useEffect } from 'react'
import { useFilePicker } from 'use-file-picker';
import {
    FileAmountLimitValidator,
    FileSizeValidator,
  } from 'use-file-picker/validators';
import { Timestamp } from 'firebase/firestore';
import { MSG_TYPE_ENUM } from './Message';
import TextareaAutosize from 'react-textarea-autosize';
import { useVisualizerContext } from './Visualizer';
import { TiPlus } from "react-icons/ti";

export default function Input({ showAutoReply, setShowAutoReply, config, inputSize, setHeight }) {
    const { handleFileUpload, addMessage, isFileUploadEnabled } = useVisualizerContext();
    const fileSettings = {
        multiple: true,
        validators: [
            new FileAmountLimitValidator({ max: 3 }),
            new FileSizeValidator({ maxFileSize: 3000000 }),
        ],
        onFilesSuccessfullySelected: async ({ plainFiles, filesContent }) => {
            console.log("Files selected");
            try {
                //console.log(JSON.stringify(plainFiles));
                if (!loading && errors.length === 0) {
                    await handleFileUpload(plainFiles, filesContent);
                }
            } catch (e) {
                addMessage({
                    text: "Error uploading files.",
                    msgType: MSG_TYPE_ENUM.ERROR,
                    createdAt: Timestamp.now(),
                    seen: false,
                });
            }
        },
    };

    const mimeTypes = useMemo(() => {
        const mimeTypeMap = {
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.png': 'image/png',
            '.gif': 'image/gif',
            '.svg': 'image/svg+xml',
            '.bmp': 'image/bmp',
            '.pdf': 'application/pdf',
            '.doc': 'application/msword',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            '.csv': 'text/csv',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.xls': 'application/vnd.ms-excel',
            '.txt': 'text/plain',
            '.ppt': 'application/vnd.ms-powerpoint',
            '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            '.mp3': 'audio/mpeg',
            '.mp4': 'video/mp4'
        };
        if (config.allowedFileTypes && config.allowedFileTypes.length > 0) {
            return config.allowedFileTypes.map(ext => 
                mimeTypeMap[ext.toLowerCase()] || ext
            );
        }
        return [];
    }, [config.allowedFileTypes]);

    if (config.allowedFileTypes && config.allowedFileTypes.length > 0) {
        fileSettings.accept = mimeTypes;
    }

    const { openFilePicker, loading, errors } = useFilePicker(fileSettings);

    const chatText = useRef("");

    async function sendMessage() {
        console.log("Sending message...")
        const text = chatText.current.value.trim();
        chatText.current.value = "";
        await addMessage({
            text,
            msgType: MSG_TYPE_ENUM.TEXT_VISITOR,
            createdAt: Timestamp.now(),
            seen: false,
        });
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim() !== "") {
            e.preventDefault();
            sendMessage();
        }
    }

    useEffect(() => {
        if (errors.length > 0) {
            let isMaxFileLimitError = false;
            let files = [];
            for (let i = 0; i < errors.length; i++) {
                console.log(errors[i]);
                if (errors[i].name === 'FileAmountLimitError') {
                    isMaxFileLimitError = true;
                } else if (errors[i].name === 'FileSizeError') {
                    files.push(errors[i].causedByFile);
                }
            }
            if (isMaxFileLimitError) {
                addMessage({
                    text: "You can only upload up to three files at a time.",
                    msgType: MSG_TYPE_ENUM.ERROR,
                    createdAt: Timestamp.now(),
                    seen: false,
                });
            } else {
                addMessage({
                    text: `File${files.length > 1 ? "s" : ""} exceeds max size of 3MB: ${files.map(file => file.name).join(", ")}`,
                    msgType: MSG_TYPE_ENUM.ERROR,
                    createdAt: Timestamp.now(),
                    seen: false,
                });
            }
        }
    }, [errors]);

    useEffect(() => {
        const updateSize = () => {
          if (inputSize.current) {
            const { height } = inputSize.current.getBoundingClientRect();
            setHeight(height);
          }
        };
    
        updateSize(); // Initial measurement is still needed
        
        // Create a MutationObserver to watch for changes to the input size
        const observer = new MutationObserver(updateSize);
        if (inputSize.current) {
            observer.observe(inputSize.current, { 
                attributes: true, 
                childList: true, 
                subtree: true 
            });
        }
        
        // Clean up observer only
        return () => {
            observer.disconnect();
        };
      }, []);

    if (!isFileUploadEnabled) {
        return (
            <div className="input" ref={inputSize}>
                <JPTextArea chatText={chatText} handleKeyDown={handleKeyDown} showAutoReply={showAutoReply} setShowAutoReply={setShowAutoReply}/>
            </div>
        );
    }

    //<textarea className="input-text-area" ref={chatText} placeholder="Send a message..." onKeyDown={handleKeyDown}/>
    //<button className="input-send-button" type="file" disabled={false/**!chatText.current || !chatText.current.value.trim()*/} onClick={() => sendMessage()} >
    //    <RiSendPlaneFill className='input-send-button-icon' />
    //</button>
    return (
        <div className="input" ref={inputSize}>
            <button className="input-button" type="file" disabled={loading} onClick={openFilePicker} >
                <TiPlus className='input-button-icon' />
            </button>
            <JPTextArea chatText={chatText} handleKeyDown={handleKeyDown} showAutoReply={showAutoReply} setShowAutoReply={setShowAutoReply} />         
        </div>
    );
}

function JPTextArea({ chatText, handleKeyDown, showAutoReply, setShowAutoReply, ...rest }) {

    function checkAutoReply() {
        if (showAutoReply) {
            setShowAutoReply(false);
        }
    }

    return (
        <TextareaAutosize 
            ref={chatText}
            onKeyDown={handleKeyDown}
            onChange={checkAutoReply}
            placeholder="Send a message..."
            maxRows={10}
            className='input-text-area'
            {...rest}
        />
    );
};
